import { motion, useReducedMotion } from "framer-motion";
import { JOURNEY } from "../../content";
import { Eyebrow, Reveal } from "./Primitives";
import { onAnchor } from "../../lib/scroll";

const TONES = {
  ice: { ring: "#9BD3F5", fill: "transparent", text: "#E0F2FE" },
  bronze: { ring: "#C98B5A", fill: "rgba(201,139,90,0.08)", text: "#F1C9A5" },
  silver: { ring: "#C9D3DE", fill: "rgba(201,211,222,0.08)", text: "#E8EEF4" },
  gold: { ring: "#D9B45A", fill: "rgba(217,180,90,0.08)", text: "#F3DC9C" },
};

const Badge = ({ n, tone }) => {
  const t = TONES[tone];
  return (
    <svg viewBox="0 0 80 80" className="h-16 w-16 sm:h-20 sm:w-20" aria-hidden="true">
      <circle cx="40" cy="40" r="37" fill={t.fill} stroke={t.ring} strokeWidth="1" strokeDasharray="2 4" />
      <circle cx="40" cy="40" r="29" fill="#0A1B33" stroke={t.ring} strokeWidth="1.25" />
      <path d="M26 46c4-5 8-5 14 0s10 5 14 0" fill="none" stroke={t.ring} strokeWidth="1.25" strokeLinecap="round" opacity="0.7" />
      <text x="40" y="38" textAnchor="middle" fontSize="13" fontWeight="800" fill={t.text} fontFamily="Mulish">{n}</text>
    </svg>
  );
};

export const Journey = () => {
  const reduce = useReducedMotion();
  return (
    <section data-testid="journey-section" className="relative overflow-hidden bg-navy py-24 text-white sm:py-32">
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.06]" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <Eyebrow dark>Der Manta-Lernpfad</Eyebrow>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
              Vom ersten Wasserkontakt <span className="font-light italic text-sky-200">bis Bronze.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-slate-300 md:text-lg">
              Jedes Kind entwickelt seine Fähigkeiten im eigenen Tempo. Der Wechsel in die nächste Stufe erfolgt, sobald die jeweiligen Anforderungen sicher erfüllt sind.
            </p>
          </Reveal>
        </div>

        <div className="relative mt-20">
          <div className="absolute left-8 top-0 h-full w-px bg-white/15 sm:left-10 lg:left-0 lg:top-10 lg:h-px lg:w-full" />
          <motion.div
            className="absolute left-8 top-0 h-full w-px origin-top bg-sky-300 sm:left-10 lg:left-0 lg:top-10 lg:h-px lg:w-full lg:origin-left"
            initial={reduce ? false : { scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 2, ease: [0.65, 0, 0.35, 1] }}
            style={{ transformOrigin: undefined }}
          />
          <ol className="relative grid gap-10 lg:grid-cols-4 lg:gap-6">
            {JOURNEY.map((s, i) => (
              <Reveal as="li" key={s.slug} delay={0.15 * i} className="flex items-center gap-6 lg:flex-col lg:items-start" >
                <a href="#kurse" onClick={onAnchor("kurse")} data-testid={`journey-step-${s.slug}`} className="group flex items-center gap-6 lg:flex-col lg:items-start">
                  <span className="relative z-10 bg-navy transition-transform duration-500 group-hover:-translate-y-1">
                    <Badge n={`0${i + 1}`} tone={s.tone} />
                  </span>
                  <span>
                    <span className="block text-xl font-bold sm:text-2xl">{s.name}</span>
                    <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Stufe {i + 1}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
